import { shapes, util } from "@joint/plus";
import { RappidNode, RappidText, RappidNodeType } from "helpers/Constants";
import { calculateAddDelayDate } from "helpers/AutomationHelper";
import { isEmpty } from "helpers/Validator";
import { automationReportStore, automationReportClientStore, automationStore, rappidStore, emailStore } from "stores";
import * as _ from "lodash";
import { isStringEmpty, isEmptyArray } from "helpers/utilityFunctions";
import StepDelay from "assets/img/icons/AutomationDelay.svg";
import StepEmail from "assets/img/icons/AutomationEmail.svg";
import StepCondition from "assets/img/icons/AutomationCondition.svg";
import StepAlert from "assets/img/icons/AutomationAlert.svg";
import StepUpdateRecord from "assets/img/icons/AutomationUpdateRecord.svg";
import StepText from "assets/img/icons/AutomationText.svg";
import StepFolder from "assets/img/automation/step-group-nobg.svg";
import StepTask from "assets/img/icons/AutomationTasks.svg";
import IconCloseHeader from "assets/img/icons/AutomationTrash.svg";
import { AutomationType, TriggerList, TriggerType,TriggerListClient } from "helpers/enum";
import { toJS } from 'mobx';
import { renderConditionText, getTimeTriggerList, renderDelayText } from "components/Automation/SidebarForm/DelayForm";
import { renderTextNodeLine1, renderTextNodeLine2, renderTextNodeLine3 } from "components/Automation/SidebarForm/TextForm";
import { renderEmailNodeLine1, renderEmailNodeLine2, renderEmailNodeLine3, renderEmailNodeLine4 } from "components/Automation/SidebarForm/EmailForm";
import { renderAlertNodeLine1, renderAlertNodeLine2, renderAlertNodeLine3, renderAlertNodeLine4 } from "components/Automation/SidebarForm/AlertForm";
import { setUpdateRecordNodeTextLine1, setUpdateRecordNodeTextLine2 } from "components/Automation/SidebarForm/UpdateRecordForm";
import { Enum } from "helpers";
import { renderFolderNodeLine1, renderFolderNodeLine2 } from "components/Automation/SidebarForm/FolderForm";
import { setTaskNodeTextLine1, setTaskNodeTextLine2 } from "components/Automation/SidebarForm/TaskStepForm";
import {
  getBodyTextWidth,
  getHeaderLabelWidth,
  applyNodeAccent,
  applyNodeIdleStyle,
  refreshCampaignNodeChrome,
} from "components/Rappid/nodes/campaignBuilder/nodeStyle";

//functions that add or remove nodes from the rappid canvas, nodes defined elsewhere

// Rappid Report Region

const TRIGGER_MARKUP_SELECTORS = ['body', 'header', 'headerDivider', 'iconBadge', 'typeIcon', 'label', 'description', 'additionalDescription'];

const setTriggerCursor = (triggerStep, cursor) => {
  TRIGGER_MARKUP_SELECTORS.forEach(selector => triggerStep.attr(`${selector}/style`, { cursor }));
};

const UpdateCompletedStepText = (type = AutomationType.Candidate) => {
  const { graph } = rappidStore;
  const { candidateTriggered, clientTriggered, candidateSteps, clientSteps, triggerList, automationAudiences } = type === AutomationType.Client ? automationReportClientStore : automationReportStore;
  const triggerStep = graph.getFirstCell();

  if (triggerList.length > 0) {
    const count = type === AutomationType.Client ? clientTriggered : candidateTriggered;
    const descText = count === 1 ? RappidText.SingleCandidateEnteredTriggerText : RappidText.CandidateEnteredTriggerText;
    triggerStep.attr('additionalDescription/text', `${count} ${descText}`);
  } else if (automationAudiences?.length > 0) {
    const total = automationAudiences.reduce((sum, audience) => sum + (audience.totalMembers || 0), 0);
    const descText = total === 1 ? RappidText.SinglePersonInFoldersText : RappidText.PeopleInFoldersText;
    triggerStep.attr('additionalDescription/text', `${total} ${descText}`);
  }
  // Folder-based triggers can't back a click-through completions list (the backend only
  // tracks entries for criteria-based triggers), so drop the pointer cursor across the whole trigger card so it doesn't look clickable.
  setTriggerCursor(triggerStep, triggerList.length > 0 ? 'pointer' : 'default');
  const successors = graph.getSuccessors(triggerStep) || [];

  if (candidateSteps && candidateSteps.length > 0) {
    successors.forEach(item => {
      const candidateStep = candidateSteps.find(x => x.stepId === item.id && x.stepType.toLowerCase() === item.attributes.roiNodeType.toLowerCase()) || {};
      const descText = candidateStep?.completedCandidatesCount === 1 ? RappidText.SingleCandidateEnteredText : RappidText.CandidateEnteredText;
      item.attr('additionalDescription/text', `${candidateStep?.completedCandidatesCount || 0} ${descText}`);
    });
  }

  if (clientSteps && clientSteps.length > 0) {
    successors.forEach(item => {
      const clientStep = clientSteps.find(x => x.stepId === item.id && x.stepType.toLowerCase() === item.attributes.roiNodeType.toLowerCase()) || {};
      const descText = clientStep?.completedClientsCount === 1 ? RappidText.SingleCandidateEnteredText : RappidText.CandidateEnteredText;
      item.attr('additionalDescription/text', `${clientStep?.completedClientsCount || 0} ${descText}`);
    });
  }

  // Filter ifelse elements only
  const ifElseElements = successors.filter(x => x.attributes.roiNodeType === RappidNodeType.IfElse) || [];
  ifElseElements.forEach(item => {
    const childs = graph.getNeighbors(item, { outbound: true }) || [];
    if (childs.length === 1) {  //TODO: If we add a final step all items will have 1 child
      const connectedLinks = graph.getConnectedLinks(item, { outbound: true }) || [];
      connectedLinks.length > 0 && connectedLinks[0].set({
        defaultLabel: {
          ...connectedLinks[0].get('defaultLabel'),
          position: {
            distance: 35,
            args: {
              absoluteDistance: true
            }
          }
        }
      });
    }
  });

  // Filter delay elements Only there is bug on 5796 when delay at first but in graph !== firstCell
  const cells = graph.getCells() || [];
  const delayElements = cells.filter(x => x.attributes.roiNodeType === RappidNodeType.Delay) || [];

  delayElements.forEach(item => {
    // Keep completion count in the footer; do not override header/body layout.
    item.attr('additionalDescription/refY', '90%');
    item.attr('additionalDescription/text', `0 ${RappidText.CandidateEnteredText}`);

    if (clientSteps && clientSteps.length > 0) {
      const element = clientSteps.find(x => x.stepId === item.id && x.stepType.toLowerCase() === item.attributes.roiNodeType.toLowerCase()) || {};
      const descText = element?.completedClientsCount === 1 ? RappidText.SingleCandidateEnteredText : RappidText.CandidateEnteredText;
      item.attr('additionalDescription/text', `${element?.completedClientsCount || 0} ${descText}`);
    }

    if (candidateSteps && candidateSteps.length > 0) {
      const element = candidateSteps.find(x => x.stepId === item.id && x.stepType.toLowerCase() === item.attributes.roiNodeType.toLowerCase()) || {};
      const descText = element?.completedCandidatesCount === 1 ? RappidText.SingleCandidateEnteredText : RappidText.CandidateEnteredText;
      item.attr('additionalDescription/text',
        `${element?.completedCandidatesCount || 0} ${descText}`
      );
    }
  });

  const textElements = cells.filter(x => x.attributes.roiNodeType === RappidNodeType.Text) || [];

  textElements.forEach(item => {
    item.attr('additionalDescription/refY', '90%');
  });

};
// End of Rappid Report Region

const CheckIfRappidCanvasIsEmpty = (isUpdateForm = false) => {
  const { graph } = rappidStore;
  const successors = graph?.getSuccessors(graph.getFirstCell());
  const countValidateAutomationType = 1;
  return isUpdateForm && !automationStore.isAutomationFormDirty ? false : successors ? successors.length === countValidateAutomationType : true;
};

// Small pill-shaped nodes (Add step, End of flow) need to render in front of any link
// touching them, or the link's line shows straight through their body - JointJS gives
// newly-added cells a higher z-index, so a link added after these nodes exist (on
// creation, or when a reorder drag reconnects to one) would otherwise draw over them.
const bringSmallPillNodesToFront = (node) => {
  const type = node?.attributes?.roiNodeType;
  if (type === RappidNode.AddStep || type === RappidNode.EndOfFlow) {
    node.toFront();
  }
};

const AddRappidLink = (source, target, dashedLine = false) => {
  const { graph } = rappidStore;
  const link = new shapes.standard.Link();
  link.attr({
    line: {
      stroke: RappidNode.LinkColor,
      strokeWidth: RappidNode.LinkStrokeWidth,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      targetMarker: 'none',
      ...(dashedLine && { strokeDasharray: '4 6' }),
    },
  });
  link.source(source);
  link.target(target);
  graph.addCells(link);
  bringSmallPillNodesToFront(source);
  bringSmallPillNodesToFront(target);
  return link;
};

const AddRappidLinkBranching = (source, target, text = RappidText.YesStep) => {
  const { graph } = rappidStore;
  const isYes = text === RappidText.YesStep;
  const link = new shapes.Roi.CustomLinkBranching();
  link.attr({
    line: {
      stroke: RappidNode.LinkColor,
      strokeWidth: RappidNode.LinkStrokeWidth,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      targetMarker: 'none'
    },
  });
  link.source(source);
  link.target(target);
  link.appendLabel({
    attrs: {
      body: {
        stroke: 'none',
        fill: isYes ? RappidNode.YesColor : RappidNode.NoColor
      },
      label: {
        text: isYes ? 'CONDITION MET' : 'OTHERWISE',
        fill: isYes ? RappidNode.YesTextColor : RappidNode.NoTextColor,
        fontSize: 9,
        fontWeight: '700',
      },
    }
  });
  graph.addCells(link);
  bringSmallPillNodesToFront(source);
  bringSmallPillNodesToFront(target);
  return link;
}

// getFirstCell() is undefined on an empty graph, and this runs straight from a render in
// AutomationFinalise, so the missing ?. took the whole page down rather than the canvas.
const CheckIfFirstTriggerCanvasIsEmpty = () => isEmpty(rappidStore.graph?.getFirstCell()?.attributes?.roiData);
const IsAddStepLastStep = (activeAddedStepElement) => rappidStore.graph.getSuccessors(activeAddedStepElement).length === 0;

const AddEmptyTriggerNodeToCanvas = (isDrawInitStep = true, roiData = {}, isDraft = true) => {
  const { graph, paper } = rappidStore;
  const { isCopiedTemplate } = automationStore;
  let deleteIcon = {
    xlinkHref: '',
    width: 14,
    height: 12,
    'ref-dx': '',
    y: '',
    roiId: '',
    event: ''
  };
  let descriptionText = RappidText.TriggerStepTitle;

  if (!_.isEmpty(roiData)) {
    deleteIcon = {
      xlinkHref: IconCloseHeader,
      width: 12,
      height: 12,
      'ref-dx': -22,
      y: RappidNode.DeleteIconY,
      roiId: `${RappidNode.DeleteStep}-${Date.now()}`,
      event: 'element:deletetrigger:pointerdown'
    };
    descriptionText = wrapBodyTitle(
      roiData?.triggerName || RappidText.TriggerEditTitle,
      RappidNode.TriggerRectWidth
    );
  }

  const initTriggerStep = new shapes.Roi.CustomTrigger({});
  initTriggerStep.attr('label/text', RappidText.TriggerStep);
  initTriggerStep.attr({
    description: {
      text: descriptionText,
      textAnchor: 'middle',
      textVerticalAnchor: 'middle',
      refX: '50%',
      x: 0,
      y: RappidNode.HeaderHeight + (RappidNode.TriggerRectHeight - RappidNode.HeaderHeight) / 2,
    },
  });
  initTriggerStep.attr('body/strokeDasharray', 'none');
  initTriggerStep.attr('deleteIcon', deleteIcon);
  initTriggerStep.set('roiData', roiData);

  graph.addCells(initTriggerStep);
  paper.findViewByModel(initTriggerStep).el.classList.add('joint-type-roi-audience');
  return isDrawInitStep ? AddToCanvas_AddStepNode(true, null, null, null, null, initTriggerStep, null, isDraft) : initTriggerStep;
};

// NB: The position of the AddStepNOde is set in AddRappidLinkBranching /AddRappidLink, values passed in here are irrelevant
const AddToCanvas_AddStepNode = (
  isInitialStep = false,
  positionX,
  positionY,
  nodeWidth,
  nodeHeight,
  sourceNode,
  branchingText,
  isDraft = true
) => {
  const { graph, paper } = rappidStore;

  var rappidStep = GetAddStepNode(isInitialStep, positionX, positionY, nodeWidth, nodeHeight, isDraft);

  const previousNode = sourceNode ?? graph.getElements()[graph.getElements().length - 1];
  graph.addCells(rappidStep);

  paper.findViewByModel(rappidStep).el.classList.add('joint-type-roi-addstep');

  if (previousNode) {
    if (previousNode.attributes.roiNodeType === RappidNodeType.IfElse) {
      const text = branchingText ?? RappidText.YesStep;
      AddRappidLinkBranching(previousNode, rappidStep, text);
    } else
      AddRappidLink(previousNode, rappidStep);
  }
  return rappidStep;
};

const AddToCanvas_EndOfFlowNode = (
  positionX,
  positionY,
  sourceNode,
) => {
  const { graph } = rappidStore;
  const x = positionX ?? RappidNode.InitX;
  const y = positionY ?? RappidNode.InitY + RappidNode.DistanceY;
  const width = 132;
  const height = 32;
  const rappidStep = new shapes.standard.Rectangle({
    position: { x, y },
    size: { width, height },
    attrs: {
      label: {
        text: "End of flow",
        fill: RappidNode.DarkGreyColor,
        fontWeight: 600,
        letterSpacing: 0.4,
        fontSize: 11,
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
        refX: '50%',
        refY: '50%',
        x: 0,
        y: 0,
      },
      body: {
        fill: '#FAFAFA',
        strokeWidth: 1,
        stroke: RappidNode.GreyColor,
        strokeDasharray: '4 4',
        rx: 999,
        ry: 999,
      },
    },
    id: `${RappidNode.EndOfFlow}-${Date.now()}`,
    roiId: `${RappidNode.EndOfFlow}-${Date.now()}`,
    roiNodeType: RappidNode.EndOfFlow
  });

  const previousNode = sourceNode ?? graph.getElements()[graph.getElements().length - 1];
  graph.addCells(rappidStep);
  AddRappidLink(previousNode, rappidStep, true);
  return rappidStep;
};

const GetAddStepNode = (isInitialStep, positionX, positionY, nodeWidth, nodeHeight, isDraft) => {
  const x = positionX ?? RappidNode.InitX;
  const y = positionY ?? RappidNode.InitY + RappidNode.DistanceY;
  const width = nodeWidth ?? isDraft ? RappidNode.AddStepWidth : 0;
  const height = nodeHeight ?? isDraft ? RappidNode.AddStepHeight : 0;

  if (isDraft) {
    return new shapes.standard.Rectangle({
      position: { x, y },
      size: { width, height },
      attrs: {
        label: {
          text: RappidText.AddStep,
          fill: RappidNode.PrimaryColor,
          fontSize: 13,
          fontWeight: '600',
          textAnchor: 'middle',
          textVerticalAnchor: 'middle',
          refX: '50%',
          refY: '50%',
          x: 0,
          y: 0,
        },
        body: {
          fill: RappidNode.WhiteColor,
          strokeWidth: 2,
          stroke: RappidNode.PrimaryColor,
          rx: 16,
          ry: 16,
          filter: RappidNode.DropShadow,
        },
      },
      id: isInitialStep ? RappidNode.InitStep : `${RappidNode.AddStep}-${Date.now()}`,
      roiId: isInitialStep ? RappidNode.InitStep : `${RappidNode.AddStep}-${Date.now()}`,
      roiNodeType: RappidNode.AddStep
    });
  } else {
    return new shapes.standard.Rectangle({
      position: { x, y },
      size: { width, height },
      id: isInitialStep ? RappidNode.InitStep : `${RappidNode.AddStep}-${Date.now()}`,
      roiId: isInitialStep ? RappidNode.InitStep : `${RappidNode.AddStep}-${Date.now()}`,
      roiNodeType: RappidNode.AddStep
    });
  }
}

const RedrawCanvas = () => {
  const { graph, paperScroller, tree } = rappidStore;
  const graphCells = graph.getCells();
  graph.clear();
  graph.addCells(graphCells);
  tree.layout();
  paperScroller.positionElement(graph.getFirstCell(), 'top')
  if (graph.getFirstCell())
    paperScroller.scrollToElement(graph.getFirstCell());
};

const setEditTriggerText = (triggerNode, isDraft) => {
  if (!triggerNode) return;
  const { paper } = rappidStore;
  triggerNode.attr('label/text', RappidText.TriggerStep);
  triggerNode.attr({
    description: {
      text: wrapBodyTitle(RappidText.TriggerEditTitle, RappidNode.TriggerRectWidth),
      textAnchor: 'middle',
      textVerticalAnchor: 'middle',
      refX: '50%',
      x: 0,
      y: RappidNode.HeaderHeight + (RappidNode.TriggerRectHeight - RappidNode.HeaderHeight) / 2,
    },
  });
  triggerNode.attr('body/strokeDasharray', 'none');
  if (isDraft === true) {
    triggerNode.attr({
      deleteIcon: {
        xlinkHref: IconCloseHeader,
        width: 12,
        height: 12,
        'ref-dx': -22,
        y: RappidNode.DeleteIconY,
        roiId: `${RappidNode.DeleteStep}-${Date.now()}`,
        event: 'element:deletetrigger:pointerdown'
      }
    });
  }
  paper.findViewByModel(triggerNode).el.classList.add('joint-type-roi-customtrigger');
}

const EditRappidNode = (roiNodeType, text, text2, text3, text4, roiData, isResetActiveElement = true, isClearTemplateData = false, targetNode = null) => {
  roiData = roiData || {};
  const activeElement = targetNode || rappidStore.activeElement;
  const { isCopiedTemplate, automationState } = automationStore;

  const isDraft = automationState === Enum.AutomationStateType.Draft;

  if (roiNodeType !== RappidNodeType.IfElse) {
    var nodeData = {
      description: text,
      description2: text2,
      description3: text3,
      description4: text4
    };

    setTextPositionOnCustomNode(activeElement, nodeData, roiNodeType);
  }

  if (isCopiedTemplate && !isClearTemplateData) {
    applyNodeIdleStyle(activeElement);
  }

  const isDelayNodeEdit = roiNodeType === RappidNodeType.Delay;
  const isIfElseNodeEdit = roiNodeType === RappidNodeType.IfElse;
  const nodeWidth = isDelayNodeEdit
    ? RappidNode.DelayRectWidth
    : isIfElseNodeEdit
      ? RappidNode.RectWidthBranching
      : RappidNode.RectWidth;

  if (isIfElseNodeEdit) {
    activeElement?.attr({
      description: {
        text: wrapBodyTitle(text || RappidText.HoverInformation, nodeWidth),
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
        refX: '50%',
        x: 0,
        y: RappidNode.HeaderHeight + (RappidNode.RectHeightBranching - RappidNode.HeaderHeight) / 2,
      },
    });
  } else {
    activeElement?.attr('description/text', wrapBodyTitle(text, nodeWidth));
    activeElement?.attr('description2/text', truncateBodyLine(text2, nodeWidth));
    activeElement?.attr('description3/text', truncateBodyLine(text3, nodeWidth));
    activeElement?.attr('description4/text', truncateBodyLine(text4, nodeWidth));
  }
  activeElement?.set('roiNodeType', roiNodeType);
  activeElement?.set('roiData', roiData);
  applyNodeAccent(activeElement);

  if (roiNodeType === RappidNodeType.Trigger)
    setEditTriggerText(activeElement, isDraft);
  if (roiNodeType === RappidNodeType.Delay && GetDelayId(activeElement, false)) { // calculate delay date if updated node in the middle or top of the tree
    automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure({ type: 'edit', editedDelayNode: activeElement }));
  } else
    automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure());

  if (roiNodeType === RappidNodeType.Delay) {
    const delayLabel = (roiData.isFirstDelay && automationStore.triggerType !== TriggerType.PlacementBased)
      ? RappidText.DelayScheduleStep
      : RappidText.DelayStep;
    activeElement?.attr('label/text', truncateHeaderLabel(delayLabel, RappidNode.DelayRectWidth));
    if (roiData.isDelayCondition)
      activeElement?.attr('description2/text', truncateBodyLine(text2, RappidNode.DelayRectWidth));
  }
  isResetActiveElement && (!targetNode || targetNode === rappidStore.activeElement) && rappidStore.resetActiveElement();
};

const DeleteNode = (deletedNode) => {
  const { graph, tree, deletingNode } = rappidStore;
  const { isCopiedTemplate } = automationStore;
  rappidStore.setDeletingNode(deletedNode);

  if (!_.isEmpty(deletingNode)) {
    const nextDelayNodeId = GetDelayId(deletedNode, false);
    const deletedNodeId = deletedNode?.id;
    const previousAddStepNode = graph.getNeighbors(deletedNode, { inbound: true });
    const previousAutomationNode = graph.getNeighbors(previousAddStepNode[0], { inbound: true });
    if (deletedNode?.attributes?.roiNodeType === RappidNodeType.IfElse) {
      graph.removeCells(graph.getSuccessors(deletedNode)); //remove all nodes below deleted node
      graph.removeCells(deletedNode);// remove deleted node
      // re-add end of flow node, unless last step was custom trigger
      if (_.cloneDeep(previousAutomationNode[0]).attributes.type !== RappidNode.RoiCustomTrigger) {
        if (previousAddStepNode.length > 0) {
          let position = previousAddStepNode[0].position();
          AddToCanvas_EndOfFlowNode(RappidNode.DistanceX, position.y + RappidNode.DistanceY, previousAddStepNode[0]);
        };
      }
    } else {
      const nextStep = graph.getNeighbors(deletedNode, { outbound: true });
      const nextAutomation = graph.getNeighbors(nextStep[0], { outbound: true });
      const deletedNodeLinks = graph.getConnectedLinks(deletedNode);
      const deletedStepLinks = graph.getConnectedLinks(nextStep[0], { outbound: true });
      graph.removeCells(deletedNode, nextStep[0], deletedNodeLinks, deletedStepLinks); //only remove the deletedNode, the rest of the nodes are reattached to the previous node
      AddRappidLink(previousAddStepNode[0], nextAutomation[0]); //add links between previous node chain and next node chain
      // if the last node is end of flow and the previous node is custom trigger, remove the end of flow node
      if (_.cloneDeep(nextAutomation[0]).attributes.roiNodeType === RappidNodeType.EndOfFlow && _.cloneDeep(previousAutomationNode[0]).attributes.type === RappidNode.RoiCustomTrigger)
        graph.removeCells(nextAutomation);
    }
    tree.layout();
    rappidStore.setDeletingNode({}); // set data to null for hide modal delete
    rappidStore.resetActiveElement();
    automationStore.setShowSideBar(false);
    automationStore.setShowSideBarForm(false);
    automationStore.resetSidebarForm();

    if (deletedNode?.attributes?.roiNodeType === RappidNodeType.Delay && nextDelayNodeId) { // calculate delay date if deleted node in the middle or top of the tree
      automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure({ type: 'delete', deletedNodeId: deletedNodeId }));
      rappidStore.resetActiveElement();
    } else
      automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure());

    if (isCopiedTemplate) {
      automationStore.isAutomationUpdatedFromTemplate();
    }
  }
};

const DeleteTrigger = (deletedNode, isWithoutConfirmation = false) => {  // TODO: Combine with AddEmptyTriggerStepToCanvas
  const { deletingNode, paper } = rappidStore;
  const { isCopiedTemplate } = automationStore;
  const automationTrigger = JSON.parse(ConvertTriggerListForBackEnd());
  const candidatePlacement = automationTrigger.find(el => el?.firstColumn?.id === TriggerList.CandidatePlacementStartDate);

  if (!isWithoutConfirmation) rappidStore.setDeletingNode(deletedNode);
  if (!_.isEmpty(deletingNode) || isWithoutConfirmation) {
    deletedNode.set('roiNodeType', RappidNodeType.Trigger);
    deletedNode?.attr('label/text', RappidText.TriggerStep);
    deletedNode?.attr({
      description: {
        text: RappidText.TriggerStepTitle,
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
        refX: '50%',
        x: 0,
        y: RappidNode.HeaderHeight + (RappidNode.TriggerRectHeight - RappidNode.HeaderHeight) / 2,
      },
    });
    deletedNode.set('roiData', {});
    deletedNode.attr('description2/text', '');
    deletedNode.attr('body/strokeDasharray', 'none');
    deletedNode.attr({
      deleteIcon: {
        xlinkHref: '',
        width: 14,
        height: 12,
        'ref-dx': '',
        y: '',
        roiId: '',
        event: ''
      }
    });
    rappidStore.setDeletingNode({}); // set data to null for hide modal delete

    if (candidatePlacement) ResetAlertPlacementOwner();
    if (!_.isEmpty(deletingNode)) {
      automationStore.setShowSideBar(false);
      automationStore.setShowSideBarForm(false);
      paper.findViewByModel(deletingNode).el.classList.remove('joint-type-roi-customtrigger'); // ADD this class for used in ui.Tooltip conditional
    }
    if (isWithoutConfirmation)
      paper.findViewByModel(deletedNode).el.classList.remove('joint-type-roi-customtrigger'); // ADD this class for used in ui.Tooltip conditional

    automationStore.setTriggerType(null);
    automationStore.setTriggerList([]);
    automationStore.setAutomationAudiencesIds([]);
    automationStore.resetSelectedAutomationAudienceIds();
    automationStore.setAutomationUserIds([]);
    automationStore.resetSelectedAutomationUserIds();
    automationStore.setAutomationOfficeIds([]);
    automationStore.resetSelectedAutomationOfficeIds();
    automationStore.setAutomationGroupIds([]);
    automationStore.resetSelectedAutomationGroupIds();
    automationStore.setIncludeRecentNoteOwner(true);

    if (isCopiedTemplate) {
      automationStore.isAutomationUpdatedFromTemplate();
    }
  }
};

const ResetAlertPlacementOwner = () => {
  const { graph } = rappidStore;
  const triggerStep = graph.getFirstCell();
  const automationSteps = graph.getSuccessors(triggerStep).filter(node => node.attributes.roiId.startsWith(RappidNode.AutomationStep)) || [];
  automationSteps.forEach(node => {
    if (node.attributes.roiNodeType === RappidNodeType.Alert)
      node?.set('roiData', { ...node.attributes.roiData, isSendToPlacementOwner: false });
  });
}

const loadTriggerNodeData = (triggerList, isReport = false, isDraft = true) => {
  const { isCopiedTemplate } = automationStore;
  const elements = rappidStore.graph.getElements();
  if (isReport || !isEmptyArray(triggerList)) {    //indicate if trigger has been set.
    setEditTriggerText(elements[0], isDraft);
    //NB: This is a terrible hack, setting form trigger roi data to trigger list because the trigger form loads the data from the roi data, of the trigger
    //TODO: fix the trigger form to load the data from the trigger list, not here, or quicker still redo app...

    var isValid = validateIsListUpdated(triggerList);

    if (isCopiedTemplate && !isValid) {
      elements[0].attr('body/strokeWidth', RappidNode.ErrorElementBorderStrokeWidth);
      elements[0].attr('body/stroke', RappidNode.TemplateErrorColor);
    }

  }
  elements[0].set('roiData', toJS(triggerList));
}

const validateIsListUpdated = (list) => {
  var isValid = true;
  var parsedList = list;

  if (Object.keys(parsedList).length < 1) {
    isValid = false;
    return;
  }



  if (!Array.isArray(parsedList)) {
    parsedList = JSON.parse(list);
  }

  parsedList.forEach(data => {
    if (data.isUpdatedFromCopiedTemplate !== true) isValid = false;
  });

  return isValid;
}

const removePreviousEndOFlowNode = (activeElement) => {
  const { graph } = rappidStore;
  if (activeElement) {
    const activeElementCell = graph.getCell(activeElement.id);
    const connectedNodesBelowActiveElement = graph.getNeighbors(activeElementCell, { outbound: true, });
    connectedNodesBelowActiveElement.forEach((node) => {
      if (_.cloneDeep(node).attributes.roiNodeType === RappidNodeType.EndOfFlow) // deep copy required to check attributes.roiNodeType
        node.remove();
    });
  }
}

const AddNodeToCanvas = (roiNodeType, nodeText, text2, text3, text4, roiData, passedAddedStepElement, id, isUserAddedNode, isDraft) => {
  roiData = roiData || {};
  const { graph, paper, tree, paperScroller, activeElement } = rappidStore;
  const activeAddedStepElement = passedAddedStepElement || activeElement;
  let { x, y } = activeAddedStepElement.attributes.position; //NB : possibly remove, not used for positioning add step anymore. Positioning is done in AddRappidLinkBranching /AddRappidLink
  y = y + RappidNode.DistanceY;
  x = x - RappidNode.DistanceX;

  removePreviousEndOFlowNode(activeAddedStepElement);

  const isAddStepLastStep = IsAddStepLastStep(activeAddedStepElement);
  const nodeData = setNodeData(roiNodeType, nodeText, text2, text3, text4, roiData);
  const newNode = createNewNode(roiNodeType, roiData, null, null, nodeData, activeAddedStepElement, isUserAddedNode, false, isDraft);

  if (id) {
    newNode.set('id', id); //set id to be the same if data loaded
  }
  graph.addCells(newNode);;
  let addStepNode;

  //after adding new node, add the add step button/s
  // NB: The position of the AddStepNOde is set in AddRappidLinkBranching /AddRappidLink, values passed in here are irrelevant
  if (roiNodeType === RappidNodeType.IfElse) {
    addStepNode = AddToCanvas_AddStepNode(false, x + RappidNode.DistanceX - RappidNode.BranchingX, y + RappidNode.DistanceY, null, null, newNode, RappidText.YesStep, isDraft);
    if (isAddStepLastStep) // don't add end of flow node if we are inserting ifelse node in the middle of automation, only add if we are adding at the end
      AddToCanvas_EndOfFlowNode(x + RappidNode.DistanceX, y + RappidNode.DistanceY, addStepNode);
    let secondAddStepNode = AddToCanvas_AddStepNode(false, x + RappidNode.DistanceX + RappidNode.BranchingX, y + RappidNode.DistanceY, null, null, newNode, RappidText.NoStep, isDraft);
    AddToCanvas_EndOfFlowNode(x + RappidNode.DistanceX, y + RappidNode.DistanceY, secondAddStepNode);
    paper.findViewByModel(newNode).el.classList.add('joint-type-roi-customnode-ifelse');  // add this new class for if-else, this class is used in ui.Tooltip
  } else {
    addStepNode = AddToCanvas_AddStepNode(false, x + RappidNode.DistanceX, y + RappidNode.DistanceY, null, null, newNode, null, isDraft);
    if (isAddStepLastStep)
      AddToCanvas_EndOfFlowNode(x + RappidNode.DistanceX, y + RappidNode.DistanceY, addStepNode); // add end of flow node if not last step
  }

  if (!isAddStepLastStep) {
    const nextElements = graph.getNeighbors(activeAddedStepElement, { outbound: true }) || [];
    const nextLines = graph.getConnectedLinks(activeAddedStepElement, { outbound: true }) || [];
    graph.removeCells(nextLines);
    AddRappidLink(addStepNode, nextElements[0]);
  }
  AddRappidLink(activeAddedStepElement, newNode);

  if (!isAddStepLastStep && roiNodeType === RappidNodeType.Delay) // calculate delay date if added node in the middle or top of the tree
    automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure({ type: 'add', newDelayNode: newNode }));
  else
    automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure());

  rappidStore.resetActiveElement();
  tree.layout();
  paperScroller.scrollToElement(newNode);
  return newNode;
};

function GetElementToLinkTo(currentElementGUID, arrayStructureData) {
  let elementToLinkTo = null;
  arrayStructureData.forEach((element) => {
    if (element.nextYes === currentElementGUID)
      elementToLinkTo = { id: element.step, isYes: true };
    if (element.nextNo === currentElementGUID)
      elementToLinkTo = { id: element.step, isYes: false, isYesEmpty: element.nextYes === null };
  });
  return elementToLinkTo;
}

const getAddStepNodeToAttachTo = (index, arrayStructure) => {
  const { graph } = rappidStore;
  let elements = graph.getElements();
  let data = JSON.parse(arrayStructure);

  const lastElement = elements[elements.length - 1];
  if (index === 0 && lastElement.attributes.roiNodeType !== RappidNodeType.EndOfFlow) return lastElement; // in the case that trigger node is only present the last element is not endOfFlow

  //to ensure we are linking to the correct element we check the array data and match the current id
  let currentElementArrayData = data[index];
  let currentElementId = currentElementArrayData.step; //step in arraydata is the id of the element in elements
  let elementWeShouldLinkTo = GetElementToLinkTo(currentElementId, data);
  if (!elementWeShouldLinkTo) return null;

  // Resolved structurally (by graph links) rather than positionally (by index into
  // graph.getElements()) - toFront() on Add step/End of flow pills re-sorts that
  // array, so a fixed offset from the parent's index no longer reliably lands on the
  // right branch once any sibling pill has been brought to front.
  const parent = graph.getCell(elementWeShouldLinkTo.id);
  if (!parent) return null;
  if (parent.attributes.roiNodeType !== RappidNodeType.IfElse) return getStepTrailingAddStep(parent);

  const yesAdd = getYesBranchAddStep(parent);
  if (elementWeShouldLinkTo.isYes) return yesAdd;
  return (graph.getNeighbors(parent, { outbound: true }) || []).find(n => n.id !== yesAdd?.id) || null;
}

/**
 * The card body lines a step shows, derived from its own roiData. Shared by loading a
 * saved automation and by duplicating a step so both render identical text.
 */
const getNodeTextLines = (roiNodeType, roiData) => {
  switch (roiNodeType) {
    case RappidNodeType.Email: {
      // Sync email name from current emails list if available
      const currentEmail = emailStore.emailsList?.find(email => email.id === roiData.emailCampaignId);
      if (currentEmail && currentEmail.name !== roiData.emailCampaignName) {
        roiData.emailCampaignName = currentEmail.name;
      }
      return [
        renderEmailNodeLine1(roiData),
        renderEmailNodeLine2(roiData),
        renderEmailNodeLine3(roiData),
        renderEmailNodeLine4(roiData),
      ];
    }
    case RappidNodeType.Text:
      return [renderTextNodeLine1(roiData.title), renderTextNodeLine2(roiData), renderTextNodeLine3(roiData), ""];
    case RappidNodeType.Delay:
      getTimeTriggerList(automationStore.triggerList, automationStore.automationType, automationStore);
      return [
        renderDelayText(
          automationStore.isTriggerPlacementStartDate,
          automationStore.isTriggerPlacementEndDate,
          automationStore.automationState,
          { roiData }
        ),
        renderConditionText(roiData?.delayCondition?.thirdColumn?.names),
        "",
        "",
      ];
    case RappidNodeType.Alert:
      return [
        renderAlertNodeLine1(roiData, automationStore.automationType, automationStore.triggerType),
        renderAlertNodeLine2(roiData),
        renderAlertNodeLine3(roiData),
        renderAlertNodeLine4(roiData),
      ];
    case RappidNodeType.UpdateRecord:
      return [setUpdateRecordNodeTextLine1(roiData), setUpdateRecordNodeTextLine2(roiData), "", ""];
    case RappidNodeType.IfElse:
      return [RappidText.HoverInformation, "", "", ""];
    case RappidNodeType.Folder:
      return [renderFolderNodeLine1(roiData), renderFolderNodeLine2(roiData), "", ""];
    case RappidNodeType.Task:
      return [setTaskNodeTextLine1(roiData), setTaskNodeTextLine2(roiData), "", ""];
    default:
      return ["", "", "", ""];
  }
};

// ROI-235: a Specific user step's card reads "From: <name>", but the user lists it is named from are
// loaded by the email form, after the canvas is drawn. Without this a saved automation opens with every
// such card on "From: Specific user". Loads the lists once and redraws only that line; the step data
// and the dirty flag are untouched.
const refreshEmailSenderNames = (graph) => {
  const emailNodes = (graph?.getElements() ?? []).filter(node => {
    const roiData = node.get('roiData');
    return node.get('roiNodeType') === RappidNodeType.Email && roiData
      && ((roiData.isFromAtsUser && roiData.atsUserId) || (roiData.isFromUser && roiData.userId));
  });
  if (emailNodes.length === 0) return Promise.resolve();

  const hasLists = automationStore.automationEmailAtsUsers?.length > 0 || automationStore.automationEmailUserIds?.length > 0;
  return (hasLists ? Promise.resolve() : automationStore.getAtsUsers())
    .then(() => emailNodes.forEach(node =>
      node.attr('description2/text', truncateBodyLine(renderEmailNodeLine2(node.get('roiData')), RappidNode.RectWidth))))
    // A failed load leaves the cards on "From: Specific user"; the step itself is unaffected.
    .catch(() => { });
};

const loadAutomationIntoCanvas = (arrayStructure, triggerList, automationState, redrawCanvas) => {
  const { graph, paperScroller, tree } = rappidStore;

  // Parse diagram structure once to get stored styles
  const storedStyles = {};
  if (automationStore.automationDiagramStructure) {
    const diagram = JSON.parse(automationStore.automationDiagramStructure);
    diagram.cells.forEach(cell => {
      if (cell.attrs?.body) {
        storedStyles[cell.id] = cell.attrs.body;
      }
    });
  }

  const isDraft = automationState === Enum.AutomationStateType.Draft;

  if (redrawCanvas) {
    graph.clear();
    AddEmptyTriggerNodeToCanvas(true, {}, isDraft);
  }

  loadTriggerNodeData(triggerList, false, isDraft);
  const data = JSON.parse(arrayStructure);
  data.forEach((element, index) => {
    const [lineOfText1, lineOfText2, lineOfText3, lineOfText4] = getNodeTextLines(element.type, element.roiData);

    var nodeToAttachTo = getAddStepNodeToAttachTo(index, arrayStructure);
    if (nodeToAttachTo) {
      const node = AddNodeToCanvas(element.type, lineOfText1, lineOfText2, lineOfText3, lineOfText4, element.roiData, nodeToAttachTo, element.step, false, isDraft);

      // Apply stored styles after node is created (error borders etc.)
      if (storedStyles[element.step]) {
        node.attr('body', storedStyles[element.step]);
      }
      refreshCampaignNodeChrome(node);
    }
  });

  graph.getElements().forEach(refreshCampaignNodeChrome);
  tree.layout();
  paperScroller.scrollToElement(graph.getFirstCell());
  refreshEmailSenderNames(graph);
}

const GetDelayId = (node = null, isPreviousDelay = true) => {
  const previousNode = node?.graph?.getNeighbors(node, isPreviousDelay ? { inbound: true } : { outbound: true });
  if (!previousNode) return ""; // return "" if no neighbor again
  return previousNode[0]?.attributes?.roiNodeType === RappidNodeType.Delay ? previousNode[0]?.id : GetDelayId(previousNode[0], isPreviousDelay);
}

const ResetDelayDate = () => { // reset delay node if load automation with status not active
  automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure({ type: 'reset' }));
  rappidStore.resetActiveElement();
}

const ConvertToDelayArrayStructure = (delayData = null) => {
  const { graph } = rappidStore;
  const triggerStep = graph.getFirstCell();
  const automationSteps = graph.getSuccessors(triggerStep).filter(node => node.attributes.roiId.startsWith(RappidNode.AutomationStep)) || [];
  const arrayStructure = [];
  const newDelayNode = delayData?.type === "add" ? delayData.newDelayNode : null;
  const editedDelayNode = delayData?.type === "edit" ? delayData.editedDelayNode : null;
  const deletedNodeId = delayData?.type === "delete" ? delayData.deletedNodeId : null;
  let isStartEdit = false;

  automationSteps.forEach((node, index) => {
    let objectStructure = {};
    objectStructure.order = index + 1;
    objectStructure.step = node.attributes.id;
    objectStructure.type = node.attributes.roiNodeType;
    objectStructure.roiData = node.attributes.roiData;
    // update previous delay id & delay date
    if (node.attributes.roiNodeType === RappidNodeType.Delay && delayData) {
      if (delayData.type === "add") {
        if (node?.id === newDelayNode?.id || isStartEdit) {
          let prevDelayNode = null;
          let startDate = null;

          if (arrayStructure.length !== 0) {
            if (isStartEdit && !node?.attributes?.roiData?.previousDelayId) { // condition for if add delay node on the top of the tree
              prevDelayNode = arrayStructure.find(el => el?.step === GetDelayId(node));
            } else if (isStartEdit && node?.attributes?.roiData?.previousDelayId === newDelayNode?.attributes?.roiData?.previousDelayId) { // condition for if add delay node in the middle of the tree
              prevDelayNode = arrayStructure.find(el => el?.step === newDelayNode?.id);
            } else // for the updated children delay node
              prevDelayNode = arrayStructure.find(el => el?.step === node?.attributes?.roiData?.previousDelayId);
            startDate = prevDelayNode?.roiData?.delayDate;
          }

          const estimationDate = calculateAddDelayDate(
            node.attributes.roiData?.weeks,
            node.attributes.roiData?.days,
            node.attributes.roiData?.hours,
            node.attributes.roiData?.minutes,
            startDate
          );

          const roiData = {
            ...node.attributes.roiData,
            delayDate: estimationDate,
            previousDelayId: prevDelayNode?.step,
            previousTotalMinutesTime: (prevDelayNode?.roiData?.previousTotalMinutesTime || 0) + (node.attributes?.roiData?.weeks * 10080) + (node.attributes?.roiData?.days * 1440) + (node.attributes?.roiData?.hours * 60) + node.attributes?.roiData?.minutes
          };
          node?.set('roiData', roiData);
          objectStructure.roiData = { ...roiData };
          isStartEdit = true;
        }
      } else if (delayData.type === "edit") {
        if (isStartEdit || node?.id === editedDelayNode?.id) {
          let prevDelayNode = null;
          let startDate = null;

          if (node?.attributes?.roiData?.previousDelayId) { // if edit first delay -> first delay node doesn't have previous delay id
            prevDelayNode = arrayStructure.find(el => el?.step === node?.attributes?.roiData?.previousDelayId);
            startDate = prevDelayNode?.roiData?.delayDate;
          }

          const estimationDate = calculateAddDelayDate(
            node.attributes.roiData?.weeks,
            node.attributes.roiData?.days,
            node.attributes.roiData?.hours,
            node.attributes.roiData?.minutes,
            startDate
          );

          const roiData = {
            ...node.attributes.roiData,
            delayDate: estimationDate,
            previousTotalMinutesTime: (prevDelayNode?.roiData?.previousTotalMinutesTime || 0) + (node.attributes?.roiData?.weeks * 10080) + (node.attributes?.roiData?.days * 1440) + (node.attributes?.roiData?.hours * 60) + node.attributes?.roiData?.minutes
          };
          node?.set('roiData', roiData);
          objectStructure.roiData = { ...roiData };
          isStartEdit = true;
        }
      } else if (delayData.type === "delete") {
        if (isStartEdit || node?.attributes?.roiData?.previousDelayId === deletedNodeId) {
          const prevDelayNodeId = node?.attributes?.roiData?.previousDelayId === deletedNodeId ? GetDelayId(node) : node?.attributes?.roiData?.previousDelayId;
          let prevDelayNode = null;
          let startDate = null;

          if (prevDelayNodeId) {
            prevDelayNode = arrayStructure.find(el => el?.step === prevDelayNodeId);
            startDate = prevDelayNode?.roiData?.delayDate;
          }

          const estimationDate = calculateAddDelayDate(
            node.attributes.roiData?.weeks,
            node.attributes.roiData?.days,
            node.attributes.roiData?.hours,
            node.attributes.roiData?.minutes,
            startDate
          );

          const roiData = {
            ...node.attributes.roiData,
            delayDate: estimationDate,
            previousDelayId: prevDelayNodeId,
            previousTotalMinutesTime: (prevDelayNode?.roiData?.previousTotalMinutesTime || 0) + (node.attributes?.roiData?.weeks * 10080) + (node.attributes?.roiData?.days * 1440) + (node.attributes?.roiData?.hours * 60) + node.attributes?.roiData?.minutes
          };
          node?.set('roiData', roiData);
          objectStructure.roiData = { ...roiData };

          isStartEdit = true;
        }
      } else if (delayData.type === "reset") {
        let prevDelayNode = null;
        let startDate = null;

        if (node?.attributes?.roiData?.previousDelayId) {
          prevDelayNode = arrayStructure.find(el => el?.step === node?.attributes?.roiData?.previousDelayId);
          startDate = prevDelayNode?.roiData?.delayDate;
        }

        const estimationDate = calculateAddDelayDate(
          node.attributes.roiData?.weeks,
          node.attributes.roiData?.days,
          node.attributes.roiData?.hours,
          node.attributes.roiData?.minutes,
          startDate
        );

        const roiData = { ...node.attributes.roiData, delayDate: estimationDate };
        node?.set('roiData', roiData);
        objectStructure.roiData = { ...roiData };
      }
    }
    arrayStructure.push(objectStructure);
  });

  return JSON.stringify(arrayStructure);
}

// Option lists (dropdown source data) are reference data reloaded at runtime, e.g.
// lookupStore.getCandidateCurrentPositionsList(). They must never be persisted into the
// saved canvas: on a database with many distinct values a single picklist can run to tens
// of thousands of entries and is repeated once per node, which is what pushed some saved
// DiagramStructure values past 20 MB and made the campaign pages slow to load.
// ConvertToArrayStructure and ConvertTriggerListForBackEnd already strip these same keys,
// so the automation engine never receives them.
// The rule for this list: a key belongs here only if the form that reads it rebuilds it on
// load, so nothing is ever read back from saved state. TriggerForm and IfElseForm do that in
// initializeDropdownOptions, DelayForm via buildDelayActivityOptions. This matters because
// the builder rehydrates roiData from ArrayStructure, so whatever is stripped is what the
// sidebar forms see on reopen.
const CANVAS_STRIPPED_OPTION_KEYS = [
  "options",
  "activityOptions",
  "clientActivityOptions",
  "currentPositionOptions",
  "currentEmployerOptions",
  "postalCodeOptions",
  "cityOptions",
  "stateOptions",
  "ratingOptions",
  "clientPositionOptions",
  "clientCityOptions",
  "clientStateOptions",
  "clientPostalCodeOptions",
  "clientCountryOptions",
  // Job lookups. ConvertTriggerListForBackEnd already strips all five, so TriggerList
  // stayed clean while the canvas kept them. TriggerForm's initializeDropdownOptions
  // reassigns every one of them on load, and IfElseForm only ever writes them.
  "jobSources",
  "jobs",
  "jobTypes",
  "jobLocations",
  "workTypes",
  // Not in the two older strip lists either, so these leaked into every saved
  // structure. clientCompanyOptions alone accounts for ~19 MB in production.
  "clientCompanyOptions",
  "documentTypeOptions",
  // Not present on this branch. sourceOptions exists on develop, countryOptions arrives
  // with PR 10028, and neither appears in production data in any of the three structures.
  // Listed now so the shared list is already correct when this reaches develop.
  "countryOptions",
  "sourceOptions"
];

// A key only belongs in the list above if BOTH hold, or stripping it breaks the UI:
//   1. it is populated from a runtime lookup (e.g. clientCompanies), and
//   2. every read is satisfied without the persisted copy, either by a fallback such as
//      (clientCompanies || item?.thirdColumn?.x || []) or by initializeDropdownOptions()
//      reassigning the key on load before any read can run.
// Deliberately excluded: interviewTypeOptions and placementOptions are read with no
// fallback, and withinLastOptions is an object of display flags, not a source list.

// One key list and one traversal across all three persistence paths, which is what stops
// them drifting apart again: clientCompanyOptions was missing from both older lists and
// TriggerList alone was carrying ~22 MB of it.
//
// All strip helpers below copy rather than mutate, so the live graph the user is still
// editing is never altered by saving. That matters beyond the canvas: the objects reaching
// ConvertToArrayStructure and ConvertTriggerListForBackEnd are the same ones the open
// sidebar reads, so stripping them in place removed keys the forms still use.
const stripColumnOptions = (column) => {
  if (!column || typeof column !== "object") return column;

  let stripped = null;
  CANVAS_STRIPPED_OPTION_KEYS.forEach(key => {
    if (key in column) {
      if (!stripped) stripped = { ...column };
      delete stripped[key];
    }
  });

  return stripped || column;
};

const stripConditionOptions = (condition) => {
  if (!condition || typeof condition !== "object") return condition;

  let stripped = null;

  ["firstColumn", "secondColumn", "thirdColumn"].forEach(name => {
    const original = condition[name];
    const cleaned = stripColumnOptions(original);
    if (cleaned !== original) {
      if (!stripped) stripped = { ...condition };
      stripped[name] = cleaned;
    }
  });

  // Delay steps nest a further condition under delayCondition.
  if (condition.delayCondition) {
    const cleanedDelay = stripConditionOptions(condition.delayCondition);
    if (cleanedDelay !== condition.delayCondition) {
      if (!stripped) stripped = { ...condition };
      stripped.delayCondition = cleanedDelay;
    }
  }

  return stripped || condition;
};

const stripRoiDataOptions = (roiData) => {
  if (Array.isArray(roiData)) {
    let changed = false;
    const next = roiData.map(item => {
      const cleaned = stripConditionOptions(item);
      if (cleaned !== item) changed = true;
      return cleaned;
    });
    return changed ? next : roiData;
  }

  return stripConditionOptions(roiData);
};

// Serialise the canvas for persistence, without the runtime-only option lists.
const BuildCanvasJson = () => {
  const { graph } = rappidStore;

  // Serialise outside the try so this fails exactly as JSON.stringify(graph.toJSON())
  // did before. Returning a placeholder here would hand the caller an empty string,
  // and the callers pass the result straight to setCanvasJson, so the saved canvas
  // would be silently wiped instead of the save failing.
  const canvas = graph.toJSON();

  try {
    if (canvas && Array.isArray(canvas.cells)) {
      canvas.cells = canvas.cells.map(cell => {
        if (!cell || !cell.roiData) return cell;
        const roiData = stripRoiDataOptions(cell.roiData);
        return roiData === cell.roiData ? cell : { ...cell, roiData };
      });
    }
  } catch (e) {
    // Sanitising must never block a save. The map above either completes or throws
    // before assigning, so canvas.cells still holds the untouched original here.
    console.warn("[RappidCanvasFunctions] Could not strip canvas option lists", e);
  }

  return JSON.stringify(canvas);
};

const ResetCanvasDelayDate = () => {
  const { graph } = rappidStore;
  const triggerStep = graph.getFirstCell();
  const automationSteps = graph.getSuccessors(triggerStep).filter(node => node.attributes.roiId.startsWith(RappidNode.AutomationStep)) || [];
  const arrayStructure = [];

  automationSteps.forEach((node, index) => {
    let objectStructure = {};
    objectStructure.order = index + 1;
    objectStructure.step = node.attributes.id;
    objectStructure.type = node.attributes.roiNodeType;
    objectStructure.roiData = node.attributes.roiData;

    if (node.attributes.roiNodeType === RappidNodeType.Delay) {
      let prevDelayNode = null;
      let startDate = null;

      if (node?.attributes?.roiData?.previousDelayId) {
        prevDelayNode = arrayStructure.find(el => el?.step === node?.attributes?.roiData?.previousDelayId);
        startDate = prevDelayNode?.roiData?.delayDate;
      }

      const estimationDate = calculateAddDelayDate(
        node.attributes.roiData?.weeks,
        node.attributes.roiData?.days,
        node.attributes.roiData?.hours,
        node.attributes.roiData?.minutes,
        startDate
      );

      const roiData = { ...node.attributes.roiData, delayDate: estimationDate };
      node?.set('roiData', roiData);
      objectStructure.roiData = { ...roiData };
    }

    arrayStructure.push(objectStructure);
  });

  return BuildCanvasJson();
}

// The ArrayStructure the builder saves, built without touching anything: no sidebar reset, nothing
// written to the graph or the stores. Roi sends it with every message so it starts from the canvas
// as the recruiter left it (ROI-412), and that must not close a step form or drop the selection.
const BuildArrayStructure = () => {
  const { graph } = rappidStore;
  const triggerStep = graph.getFirstCell();
  const automationSteps = graph.getSuccessors(triggerStep).filter(node => node.attributes.roiId.startsWith(RappidNode.AutomationStep)) || [];
  const arrayStructure = [];

  automationSteps.forEach((node, index) => {
    let objectStructure = {};
    objectStructure.order = index + 1;
    objectStructure.step = node.attributes.id;
    objectStructure.type = node.attributes.roiNodeType;
    // Same walk the canvas uses, so every node type and the condition nested under a Delay
    // step get stripped, not just the three top-level columns of an If/Else. Sharing the key
    // list was only half of it: while the traversals differed, the next lookup list added to
    // a delay condition would be dropped from the canvas and kept here, which is the drift
    // this is meant to end. stripRoiDataOptions is copy-on-write, so node.attributes.roiData,
    // shared by reference with automationStore.formIfElse, is left intact for the open
    // sidebar, and the large option arrays are never cloned just to be deleted.
    objectStructure.roiData = stripRoiDataOptions(node.attributes.roiData);

    if (node.attributes.roiNodeType === RappidNodeType.IfElse) {
      const ifElseOptionNodes = graph.getNeighbors(node, { outbound: true }); // index 0 = YES, index 1 = NO
      objectStructure.nextYes = FilterNextStep(graph.getSuccessors(ifElseOptionNodes[0])[0]?.attributes.id);
      objectStructure.nextNo = FilterNextStep(graph.getSuccessors(ifElseOptionNodes[1])[0]?.attributes.id);
      objectStructure.roiData = objectStructure.roiData.map(({ isShowWithinLast, ...rest }) => rest);
    } else
      objectStructure.nextYes = FilterNextStep(graph.getSuccessors(node)[1]?.attributes.id);

    arrayStructure.push(objectStructure);
  });

  return JSON.stringify(arrayStructure);
};

const ConvertToArrayStructure = () => {
  const arrayStructure = BuildArrayStructure();

  automationStore.resetAutomationSidebarFormDirty();
  automationStore.setShowSideBar(false);
  automationStore.setShowSideBarForm(false);
  automationStore.resetSidebarForm();
  automationStore.setSidebarFormType("");
  rappidStore.resetActiveElement();

  return arrayStructure;
};

const FilterNextStep = (attributeId) => {
  return attributeId.startsWith('endOfFlow-') ? null : attributeId;
}

const GetArrayStructure = () => {
  const { graph } = rappidStore;
  const triggerStep = graph.getFirstCell();
  const automationSteps = graph.getSuccessors(triggerStep).filter(node => node.attributes.roiId.startsWith(RappidNode.AutomationStep)) || [];
  const arrayStructure = [];

  automationSteps.forEach((node, index) => {
    let objectStructure = {};
    objectStructure.order = index + 1;
    objectStructure.step = node.attributes.id;
    objectStructure.type = node.attributes.roiNodeType;
    objectStructure.roiData = node.attributes.roiData;

    if (node.attributes.roiNodeType === RappidNodeType.IfElse) {
      const ifElseOptionNodes = graph.getNeighbors(node, { outbound: true }); // index 0 = YES, index 1 = NO
      objectStructure.nextYes = graph.getSuccessors(ifElseOptionNodes[0])[0]?.attributes.id ?? null;
      objectStructure.nextNo = graph.getSuccessors(ifElseOptionNodes[1])[0]?.attributes.id ?? null;
    } else
      objectStructure.nextYes = graph.getSuccessors(node)[1]?.attributes.id ?? null;

    arrayStructure.push(objectStructure);
  });

  return JSON.stringify(arrayStructure);
};

// The TriggerList the builder saves. flattenCustomFields is the save's rewrite of firstColumn.id to
// 13 below. Roi turns it off (ROI-412), so each row keeps the column the recruiter picked; the save
// keeps it on, and its output is unchanged.
const BuildTriggerList = ({ flattenCustomFields }) => {
  const { triggerList } = automationStore;
  // Copy-on-write rather than a deep clone. [...triggerList] is shallow, so the trigger
  // objects are the ones TriggerForm is still reading, and stripping them in place deleted
  // keys it needs: the unguarded [...thirdColumn.jobSources] reads there are the same hazard
  // as IfElseForm's workTypes. A JSON round trip fixed that but duplicated the option lists
  // in full before deleting them, on the one path whose premise is that they are enormous.
  // stripConditionOptions only copies the objects that actually carry a stripped key. The
  // extra firstColumn spread is for the element.firstColumn.id = 13 write further down, so
  // that no longer reaches automationStore.triggerList either: a custom field trigger now
  // keeps its real id in the live form after a save instead of being flattened to 13.
  // ROI-64 (promoted to demo by cherry-pick): secondColumn and thirdColumn are copied
  // too. stripConditionOptions only copies a column it actually strips, so a column with
  // nothing to strip is still the live object automationStore.triggerList holds, and the
  // aggregate status "Select All" conversion below writes thirdColumn.jobStatusIds /
  // placementStatusIds. Copying only when present keeps an absent column absent, so the
  // payload is unchanged for every trigger that has no second or third column.
  let triggerListData = triggerList.map(element => {
    const stripped = stripConditionOptions(element);
    return {
      ...stripped,
      firstColumn: { ...stripped.firstColumn },
      ...(stripped.secondColumn && { secondColumn: { ...stripped.secondColumn } }),
      ...(stripped.thirdColumn && { thirdColumn: { ...stripped.thirdColumn } }),
    };
  });

  triggerListData.forEach(element => {
    const triggerId = Number(element.firstColumn.id);
    // The aggregate Job status / Placement status triggers (ROI-64) keep the full
    // status id list when "Select All" is used, purely so every checkbox stays
    // ticked in the picker. The backend contract requires "any status" to be
    // submitted as an empty array, so convert it here right before it's sent.
    // Optional chaining: not every trigger has a thirdColumn, and nothing else in this
    // function assumes one, so an unguarded read here would throw on save for those.
    if (element.thirdColumn?.isActivityAllSelected) {
      if (triggerId === TriggerListClient.ClientAggregateJobStatus) {
        element.thirdColumn.jobStatusIds = [];
      } else if (triggerId === TriggerListClient.ClientAggregatePlacementStatus) {
        element.thirdColumn.placementStatusIds = [];
      }
    }
    if (flattenCustomFields &&
      triggerId > 13 &&
      triggerId !== TriggerList.CandidatePlacementEndDate &&
      triggerId !== TriggerList.CandidatePlacementCreatedDate &&
      triggerId !== TriggerList.EmailAddress &&
      triggerId !== TriggerList.MobileNumber &&
      triggerId !== TriggerList.Birthday &&
      triggerId !== TriggerList.ApplicationStatus &&
      triggerId !== TriggerList.JobStatus &&
      triggerId !== TriggerList.InterviewDate &&
      triggerId !== TriggerList.Availability &&
      triggerId !== TriggerList.JobJobApplicationReceived &&
      triggerId !== TriggerList.PlacementJobApplicationReceived &&
      triggerId !== TriggerList.DateCreated &&
      triggerId !== TriggerList.DateUpdated &&
      triggerId !== TriggerList.CandidateCountry &&
      triggerId !== TriggerList.SecondaryEmailAddress &&
      triggerId !== TriggerList.SurveyScore &&
      triggerId !== TriggerListClient.SurveyScore &&
      triggerId !== TriggerList.PhoneNumber &&
      triggerId !== TriggerList.CandidateSkills &&
      triggerId !== TriggerList.CandidateJobCategories &&
      triggerId !== TriggerList.CandidateJobWorkType &&
      triggerId !== TriggerList.CandidateJobTypes &&
      triggerId !== TriggerList.CandidateJobId &&
      triggerId !== TriggerList.CandidateJobLocation &&
      triggerId !== TriggerList.CandidateCurrentPosition &&
      triggerId !== TriggerList.CandidateCurrentEmployer &&
      triggerId !== TriggerList.CandidatePostalCode &&
      triggerId !== TriggerList.CandidateCity &&
      triggerId !== TriggerList.CandidateState &&
      triggerId !== TriggerList.CandidateRating &&
      triggerId !== TriggerList.DocumentExpiryDate &&
      triggerId !== TriggerList.PlacementDocumentExpiryDate &&
      triggerId !== TriggerList.JobDocumentExpiryDate &&
      triggerId !== TriggerList.FormScore &&
      triggerId !== TriggerList.PlacementFormScore &&
      triggerId !== TriggerList.JobFormScore &&
      triggerId !== TriggerListClient.FormScore &&
      triggerId !== TriggerListClient.ClientPlacementFormScore &&
      triggerId !== TriggerListClient.ClientJobFormScore &&
      triggerId !== TriggerListClient.ClientJobWorkType &&
      triggerId !== TriggerListClient.ClientJobCategories &&
      triggerId !== TriggerListClient.ClientJobTypes &&
      triggerId !== TriggerListClient.ClientJobId &&
      triggerId !== TriggerListClient.ClientJobLocation &&
      triggerId !== TriggerList.CandidateCustomFieldDate &&
      triggerId !== TriggerListClient.ClientCustomFieldDate &&
      triggerId !== TriggerList.CandidateUnsubscribe &&
      triggerId !== TriggerListClient.ClientUnsubscribe &&
      triggerId !== TriggerList.PlacementStatus &&
      triggerId !== TriggerListClient.ClientPlacementStatus &&
      triggerId !== TriggerListClient.ClientAggregateJobStatus &&
      triggerId !== TriggerListClient.ClientAggregatePlacementStatus &&
      triggerId !== TriggerList.PlacementType &&
      triggerId !== TriggerListClient.ClientPlacementType &&
      triggerId !== TriggerList.CandidateSource &&
      triggerId !== TriggerList.CandidateContactMethod &&
      triggerId !== TriggerList.CandidateSeeking &&
      triggerId !== TriggerList.JobDateCreated &&
      triggerId !== TriggerList.JobDateUpdated &&
      triggerId !== TriggerListClient.ClientJobDateCreated &&
      triggerId !== TriggerListClient.ClientJobDateUpdated &&
      triggerId !== TriggerList.PlacementCustomFieldList &&
      triggerId !== TriggerList.PlacementCustomFieldMultiSelectList &&
      triggerId !== TriggerList.JobCustomFieldList &&
      triggerId !== TriggerList.JobCustomFieldMultiSelectList &&
      triggerId !== TriggerListClient.ClientPlacementCustomFieldList &&
      triggerId !== TriggerListClient.ClientPlacementCustomFieldMultiSelectList &&
      triggerId !== TriggerListClient.ClientJobCustomFieldList &&
      triggerId !== TriggerListClient.ClientJobCustomFieldMultiSelectList &&
      triggerId !== TriggerList.ApplicationCustomFieldList &&
      triggerId !== TriggerList.ApplicationCustomFieldMultiSelectList &&
      triggerId !== TriggerList.PlacementCustomFieldDate &&
      triggerId !== TriggerList.ApplicationCustomFieldDate &&
      triggerId !== TriggerList.JobCustomFieldDate &&
      triggerId !== TriggerListClient.ClientPlacementCustomFieldDate &&
      triggerId !== TriggerListClient.ClientJobCustomFieldDate &&
      triggerId !== TriggerList.CandidateDocumentType &&
      triggerId !== TriggerList.CandidateNps &&
      triggerId !== TriggerListClient.ClientSkills &&
      triggerId !== TriggerListClient.ClientEmailAddress &&
      triggerId !== TriggerListClient.ClientSecondaryEmailAddress &&
      triggerId !== TriggerListClient.ClientPhoneNumber &&
      triggerId !== TriggerListClient.ClientMobileNumber &&
      triggerId !== TriggerList.JobRoiAiActivity &&
      triggerId !== TriggerList.PlacementRoiAiActivity &&
      triggerId !== TriggerListClient.ClientJobRoiAiActivity &&
      triggerId !== TriggerListClient.ClientPlacementRoiAiActivity) {
      element.firstColumn.id = 13;  /* [NOTE] set all custom field id is 13 */
    }
  });

  return JSON.stringify(triggerListData);
}

const ConvertTriggerListForBackEnd = () => BuildTriggerList({ flattenCustomFields: true });


const createNewNode = (roiNodeType, roiData, positionX = null, positionY = null, nodeData = {}, node = null, isUserAddedNode = false, isReport = false, isDraft = false) => {
  roiData = roiData || {};
  const { activeElement } = rappidStore;
  const { isCopiedTemplate } = automationStore;
  let { x = 0, y = 0 } = node !== null ? node.attributes.position : activeElement.attributes.position;
  y = positionY ?? y + RappidNode.DistanceY;
  x = positionX ?? x - RappidNode.DistanceX;

  let createdNode;
  if (roiNodeType === RappidNodeType.IfElse) {
    createdNode = new shapes.Roi.CustomBranching();
  } else if (roiNodeType === RappidNodeType.Delay) {
    createdNode = new shapes.Roi.CustomDelayNode();
  } else
    createdNode = new shapes.Roi.CustomNode();

  if (roiNodeType !== RappidNodeType.IfElse) //custom nodes or delay nodes
    setTextPositionOnCustomNode(createdNode, nodeData, roiNodeType, isReport);

  if (!isDraft) {
    createdNode.attr('deleteIcon/xlinkHref', null);
    createdNode.attr('dragHandle/xlinkHref', null);
    if (roiNodeType !== RappidNodeType.IfElse) createdNode.attr('duplicateIcon/xlinkHref', null);
  }

  const isDelayNode = roiNodeType === RappidNodeType.Delay;
  const isIfElseNode = roiNodeType === RappidNodeType.IfElse;
  const nodeWidth = isDelayNode
    ? RappidNode.DelayRectWidth
    : isIfElseNode
      ? RappidNode.RectWidthBranching
      : RappidNode.RectWidth;

  createdNode.attr('label/text', truncateHeaderLabel(nodeData?.label || '', nodeWidth));
  if (isIfElseNode) {
    createdNode.attr({
      description: {
        text: wrapBodyTitle(nodeData?.description || RappidText.HoverInformation, nodeWidth),
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
        refX: '50%',
        x: 0,
        y: RappidNode.HeaderHeight + (RappidNode.RectHeightBranching - RappidNode.HeaderHeight) / 2,
      },
    });
  } else {
    createdNode.attr('description/text', wrapBodyTitle(nodeData?.description, nodeWidth));
    createdNode.attr('description2/text', truncateBodyLine(nodeData?.description2, nodeWidth));
    createdNode.attr('description3/text', truncateBodyLine(nodeData?.description3, nodeWidth));
    createdNode.attr('description4/text', truncateBodyLine(nodeData?.description4, nodeWidth));
  }
  createdNode.attr('typeIcon/xlinkHref', nodeData?.icon);
  applyNodeAccent(createdNode);
  createdNode.set('roiNodeType', roiNodeType);
  createdNode.set('roiData', roiData);
  createdNode.set({ position: { x, y } });


  if (isCopiedTemplate && !isUserAddedNode) {
    var isValid = true;
    if (roiNodeType !== RappidNodeType.IfElse) {
      isValid = roiData.isUpdatedFromCopiedTemplate === true
    } else {
      isValid = validateIsListUpdated(roiData);
    }

    if (!isValid && !isReport) {
      createdNode.attr('body/strokeWidth', RappidNode.ErrorElementBorderStrokeWidth);
      createdNode.attr('body/stroke', RappidNode.TemplateErrorColor);
    }
  }

  return createdNode;
};

const setNodeData = (roiNodeType, nodeText, text2, text3, text4, roiData = null) => {
  let nodeData = {
    icon: StepEmail,
    backgroundColor: RappidNode.WhiteColor,
    label: RappidText.EmailStep,
    description: nodeText,
    description2: text2,
    description3: text3,
    description4: text4
  };

  switch (roiNodeType) {
    case RappidNodeType.Email:
      nodeData = { ...nodeData, icon: StepEmail, label: RappidText.EmailStep };
      break;
    case RappidNodeType.Delay:
      nodeData = {
        ...nodeData,
        icon: StepDelay,
        label: (automationStore.triggerType !== TriggerType.PlacementBased && roiData.isFirstDelay) ? RappidText.DelayScheduleStep : RappidText.DelayStep,
        description2: text2
      };
      break;
    case RappidNodeType.Alert:
      nodeData = { ...nodeData, icon: StepAlert, label: RappidText.AlertStep };
      break;
    case RappidNodeType.IfElse:
      nodeData = { ...nodeData, icon: StepCondition, label: RappidText.IfElseStep };
      break;
    case RappidNodeType.UpdateRecord:
      nodeData = { ...nodeData, icon: StepUpdateRecord, label: RappidText.UpdateRecordStep };
      break;
    case RappidNodeType.Text:
      nodeData = { ...nodeData, icon: StepText, label: RappidText.TextStep };
      break;
    case RappidNodeType.Folder:
      nodeData = { ...nodeData, icon: StepFolder, label: RappidText.FolderStep };
      break;
    case RappidNodeType.Task:
      nodeData = { ...nodeData, icon: StepTask, label: RappidText.TaskStep };
      break;
    default:
      break;
  }

  return nodeData;
};

const setTextPositionOnCustomNode = (node, nodeData, roiNodeType, isReport = false) => {
  // Header: circular badge + type label (+ controls in builder). Body: left-aligned under the divider.
  // Same layout for builder and automation report.
  const labelX = RappidNode.IconBadgeX + RappidNode.IconBadgeSize + RappidNode.IconTextGap;
  const bodyTextX = RappidNode.CardPadding;
  const headerRowCenterY = RappidNode.IconBadgeY + RappidNode.IconBadgeSize / 2;
  const titleTop = RappidNode.HeaderHeight + RappidNode.BodyPaddingY;
  const nodeWidth = node?.size?.()?.width || RappidNode.RectWidth;
  const wrappedTitle = wrapBodyTitle(nodeData?.description, nodeWidth);
  const titleLineCount = Math.max(1, Math.min(
    RappidNode.TitleMaxLines,
    wrappedTitle ? wrappedTitle.split('\n').length : 1
  ));
  const metaStartY = titleTop
    + (RappidNode.TitleLineHeight * titleLineCount)
    + RappidNode.MetaGapAfterTitle;
  const metaLineHeight = RappidNode.MetaLineHeight;

  node.attr('label/x', labelX);
  node.attr('label/y', headerRowCenterY);
  node.attr('label/textVerticalAnchor', 'middle');
  node.attr('label/textAnchor', 'left');
  // Clear legacy report refY offsets that pull the header title out of the band
  node.attr('label/refY', null);
  node.attr('description/refY', null);
  node.attr('description2/refY', null);
  node.attr('description3/refY', null);
  node.attr('description4/refY', null);

  node.attr('description/x', bodyTextX);
  node.attr('description/y', titleTop);
  node.attr('description/textVerticalAnchor', 'top');
  node.attr('description/textAnchor', 'left');
  node.attr('description2/x', bodyTextX);
  node.attr('description3/x', bodyTextX);
  node.attr('description4/x', bodyTextX);
  node.attr('description2/textVerticalAnchor', 'top');
  node.attr('description3/textVerticalAnchor', 'top');
  node.attr('description4/textVerticalAnchor', 'top');

  switch (numberOfLinesOfTextInNode(nodeData)) {
    case 0:
    case 1:
      break;
    case 2:
      node.attr('description2/y', metaStartY);
      break;
    case 3:
      node.attr('description2/y', metaStartY);
      node.attr('description3/y', metaStartY + metaLineHeight);
      break;
    case 4:
      node.attr('description2/y', metaStartY);
      node.attr('description3/y', metaStartY + metaLineHeight);
      node.attr('description4/y', metaStartY + metaLineHeight * 2);
      break;
    default:
      break;
  }
};

function numberOfLinesOfTextInNode(nodeData) {
  let lastNotEmpty = 0;
  if (!isStringEmpty(nodeData?.description)) lastNotEmpty = 1;
  if (!isStringEmpty(nodeData?.description2)) lastNotEmpty = 2;
  if (!isStringEmpty(nodeData?.description3)) lastNotEmpty = 3;
  if (!isStringEmpty(nodeData?.description4)) lastNotEmpty = 4;
  return lastNotEmpty;
}

const wrapText = (text = "", width = RappidNode.RectWidth, height = RappidNode.RectHeight, fontSize = RappidNode.DescriptionFontSize, ellipsis = true) =>
  util.breakText(text || "", { width, height }, { 'font-size': fontSize }, { ellipsis });

/** Title/detail line 1: wrap to at most TitleMaxLines within the card text column. */
const wrapBodyTitle = (text = "", nodeWidth = RappidNode.RectWidth) =>
  wrapText(
    text,
    getBodyTextWidth(nodeWidth),
    RappidNode.TitleLineHeight * RappidNode.TitleMaxLines,
    RappidNode.TitleFontSize
  );

/** Meta lines: single-line ellipsis to the card text column width. */
const truncateBodyLine = (text = "", nodeWidth = RappidNode.RectWidth) => {
  if (text === null || text === undefined || text === "") return "";
  return wrapText(
    String(text),
    getBodyTextWidth(nodeWidth),
    RappidNode.MetaLineHeight,
    RappidNode.DescriptionFontSize
  );
};

/** Header type label: single line, leave room for delete icon. */
const truncateHeaderLabel = (text = "", nodeWidth = RappidNode.RectWidth) => {
  const label = String(text || "").toUpperCase();
  if (!label) return "";
  return wrapText(
    label,
    getHeaderLabelWidth(nodeWidth),
    RappidNode.TypeLabelFontSize * 1.4,
    RappidNode.TypeLabelFontSize
  );
};

// campaign is { automationType, triggerType } from the report's own store: automationStore holds
// whichever builder was open last, so the report cannot read the campaign's types from it (ROI-412).
const loadAutomationReportIntoCanvas = (arrayStructure, triggerList, expectedGraph, campaign = {}) => {
  const { graph, tree, paperScroller } = rappidStore;
  if (expectedGraph && graph !== expectedGraph) return;
  graph.clear();
  AddEmptyTriggerNodeToReportCanvas(true);
  loadTriggerNodeData(triggerList, true, false);
  const data = JSON.parse(arrayStructure);
  data.forEach((element, index) => {
    let lineOfText1 = "";
    let lineOfText2 = "";
    let lineOfText3 = "";
    let lineOfText4 = "";
    switch (element.type) {
      case "email":
        // Sync email name from current emails list if available
        const currentEmailReport = emailStore.emailsList?.find(email => email.id === element.roiData.emailCampaignId);
        if (currentEmailReport && currentEmailReport.name !== element.roiData.emailCampaignName) {
          element.roiData.emailCampaignName = currentEmailReport.name;
        }
        lineOfText1 = renderEmailNodeLine1(element.roiData);
        lineOfText2 = renderEmailNodeLine2(element.roiData);
        lineOfText3 = renderEmailNodeLine3(element.roiData);
        lineOfText4 = renderEmailNodeLine4(element.roiData);
        break;
      case "text":
        lineOfText1 = renderTextNodeLine1(element.roiData.title);
        lineOfText2 = renderTextNodeLine2(element.roiData);
        lineOfText3 = renderTextNodeLine3(element.roiData);
        break;
      case "delay":
        getTimeTriggerList(automationStore.triggerList, automationStore.automationType, automationStore);
        lineOfText1 = renderDelayText(automationStore.isTriggerPlacementStartDate, automationStore.isTriggerPlacementEndDate, automationStore.automationState, element);
        lineOfText2 = renderConditionText(element.roiData?.delayCondition?.thirdColumn?.names);
        break;
      case "alert":
        lineOfText1 = renderAlertNodeLine1(element.roiData, campaign.automationType, campaign.triggerType);
        lineOfText2 = renderAlertNodeLine2(element.roiData);
        lineOfText3 = renderAlertNodeLine3(element.roiData);
        lineOfText4 = renderAlertNodeLine4(element.roiData);
        break;
      case "updateRecord":
        lineOfText1 = setUpdateRecordNodeTextLine1(element.roiData);
        lineOfText2 = setUpdateRecordNodeTextLine2(element.roiData);
        break;
      case "usertask":
        lineOfText1 = setTaskNodeTextLine1(element.roiData);
        lineOfText2 = setTaskNodeTextLine2(element.roiData);
        break;
      case "ifelse":
        lineOfText1 = RappidText.HoverInformation;
        break;
      case "folder":
        lineOfText1 = renderFolderNodeLine1(element.roiData);
        lineOfText2 = renderFolderNodeLine2(element.roiData);
        break;
      default:
    }

    var nodeToAttachTo = getAddStepNodeToAttachTo(index, arrayStructure);
    if (nodeToAttachTo)
      AddNodeToReportCanvas(element.type, lineOfText1, lineOfText2, lineOfText3, lineOfText4, element.roiData, nodeToAttachTo, element.step);
  });

  tree.layout();
  paperScroller.positionElement(graph.getFirstCell(), 'top');
  paperScroller.scrollToElement(graph.getFirstCell());
  refreshEmailSenderNames(graph);
}

const AddNodeToReportCanvas = (roiNodeType, nodeText, text2, text3, text4, roiData, passedAddedStepElement, id) => {
  roiData = roiData || {};
  const { graph, paper, activeElement } = rappidStore;
  const activeAddedStepElement = passedAddedStepElement || activeElement;
  let { x, y } = activeAddedStepElement.attributes.position; //NB : possibly remove, not used for positioning add step anymore. Positioning is done in AddRappidLinkBranching /AddRappidLink
  y = y + RappidNode.DistanceY;
  x = x - RappidNode.DistanceX;

  removePreviousEndOFlowNode(activeAddedStepElement);

  const isAddStepLastStep = IsAddStepLastStep(activeAddedStepElement);
  const nodeData = setNodeData(roiNodeType, nodeText, text2, text3, text4, roiData);
  const newNode = createNewNode(roiNodeType, roiData, null, null, nodeData, activeAddedStepElement, false, true);
  if (id) {
    newNode.set('id', id); //set id to be the same if data loaded
  }
  graph.addCells(newNode);;
  let addStepNode;

  //after adding new node, add the add step button/s
  // NB: The position of the AddStepNOde is set in AddRappidLinkBranching /AddRappidLink, values passed in here are irrelevant
  if (roiNodeType === RappidNodeType.IfElse) {
    addStepNode = AddToReportCanvas_AddStepNode(false, x + RappidNode.DistanceX - RappidNode.BranchingX, y + RappidNode.DistanceY, newNode, RappidText.YesStep);

    if (isAddStepLastStep) // don't add end of flow node if we are inserting ifelse node in the middle of automation, only add if we are adding at the end
      AddToCanvas_EndOfFlowNode(x + RappidNode.DistanceX, y + RappidNode.DistanceY, addStepNode);

    let secondAddStepNode = AddToReportCanvas_AddStepNode(false, x + RappidNode.DistanceX + RappidNode.BranchingX, y + RappidNode.DistanceY, newNode, RappidText.NoStep);
    AddToCanvas_EndOfFlowNode(x + RappidNode.DistanceX, y + RappidNode.DistanceY, secondAddStepNode);

    paper.findViewByModel(newNode).el.classList.add('joint-type-roi-customnode-ifelse');  // add this new class for if-else, this class is used in ui.Tooltip
  } else {
    addStepNode = AddToReportCanvas_AddStepNode(false, x + RappidNode.DistanceX, y + RappidNode.DistanceY, newNode);
    if (isAddStepLastStep)
      AddToCanvas_EndOfFlowNode(x + RappidNode.DistanceX, y + RappidNode.DistanceY, addStepNode); // add end of flow node if not last step
  }

  if (!isAddStepLastStep) {
    const nextElements = graph.getNeighbors(activeAddedStepElement, { outbound: true }) || [];
    const nextLines = graph.getConnectedLinks(activeAddedStepElement, { outbound: true }) || [];
    graph.removeCells(nextLines);
    AddRappidLink(addStepNode, nextElements[0]);
  }
  AddRappidLink(activeAddedStepElement, newNode);

  if (!isAddStepLastStep && roiNodeType === RappidNodeType.Delay) // calculate delay date if added node in the middle or top of the tree
    automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure({ type: 'add', newDelayNode: newNode }));
  else
    automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure());

  rappidStore.resetActiveElement();
  return newNode;
};

const AddToReportCanvas_AddStepNode = (
  isInitialStep = false,
  positionX,
  positionY,
  sourceNode,
  branchingText,
) => {
  const { graph, paper } = rappidStore;
  const x = positionX ?? RappidNode.InitX;
  const y = positionY ?? RappidNode.InitY + RappidNode.DistanceY;
  const width = 0;
  const height = 0;
  const rappidStep = new shapes.standard.Rectangle({
    position: { x, y },
    size: { width, height },
    id: isInitialStep ? RappidNode.InitStep : `${RappidNode.AddStep}-${Date.now()}`,
    roiId: isInitialStep ? RappidNode.InitStep : `${RappidNode.AddStep}-${Date.now()}`,
    roiNodeType: RappidNode.AddStep
  });

  const previousNode = sourceNode ?? graph.getElements()[graph.getElements().length - 1];

  graph.addCells(rappidStep);

  paper.findViewByModel(rappidStep).el.classList.add('joint-type-roi-addstep');

  if (previousNode) {
    if (previousNode.attributes.roiNodeType === RappidNodeType.IfElse) {
      const text = branchingText ?? RappidText.YesStep;
      AddRappidLinkBranching(previousNode, rappidStep, text);
    } else
      AddRappidLink(previousNode, rappidStep);
  }
  return rappidStep;
};

const AddEmptyTriggerNodeToReportCanvas = (isDrawInitStep = true, roiData = {}) => {
  const { graph, paper } = rappidStore;
  let deleteIcon = {
    xlinkHref: '',
    'ref-dx': '',
    'ref-y': '',
    roiId: '',
    event: ''
  };

  const initTriggerStep = new shapes.Roi.CustomTrigger({});
  initTriggerStep.attr('description/text', '');
  initTriggerStep.attr('deleteIcon', deleteIcon);
  initTriggerStep.set('roiData', roiData);
  graph.addCells(initTriggerStep);
  paper.findViewByModel(initTriggerStep).el.classList.add('joint-type-roi-audience');

  return isDrawInitStep ? AddToReportCanvas_AddStepNode(true, null, null, initTriggerStep) : initTriggerStep;
};

const getTriggerNodeData = () => {
  const { graph } = rappidStore;
  if(!graph) return [];

  // The graph being present does not mean it has drawn anything: getFirstCell() is undefined
  // until it has. This is called from the header's disabled check on every render, so an empty
  // graph crashed the builder instead of just disabling a button.
  return graph.getFirstCell()?.attributes?.roiData;
}
const getAllNodes = () => {
  const { graph } = rappidStore;
  if(!graph) return [];
  const triggerStep = graph.getFirstCell();
  // Get all successors of trigger step that are automation steps (excludes utility nodes)
  return graph.getSuccessors(triggerStep)
    .filter(node => node.attributes.roiId.startsWith(RappidNode.AutomationStep)) || [];
};

const getNodesInfo = () => {
  const nodes = getAllNodes();
  return nodes.map((node, index) => ({
    order: index + 1,
    id: node.attributes.id,
    type: node.attributes.roiNodeType,
    data: node.attributes.roiData,
    hasError: nodeHasErrorBorder(node)
  }));
};


const setBorderColorForNode = (nodeId, color, width = 2) => {
  const { graph } = rappidStore;
  if (!graph) return false;
  const node = graph.getCell(nodeId);
  if (!node) return false;

  node.attr('body/stroke', color);
  node.attr('body/strokeWidth', width);
  // Keep elevation / selection filter coherent with border state
  if (color === RappidNode.PrimaryColor) {
    node.attr('body/filter', RappidNode.SelectedGlow);
  } else {
    node.attr('body/filter', RappidNode.DropShadow);
  }

  return true;
};

// Helper function to reset border to default
const resetBorderForNode = (nodeId) => {
  const { graph } = rappidStore;
  if (!graph) return false;
  const node = graph.getCell(nodeId);

  if (!node) return false;

  applyNodeIdleStyle(node);
  return true;
};

const setErrorBorder = (nodeId) => {
  return setBorderColorForNode(nodeId, RappidNode.TemplateErrorColor, RappidNode.ErrorElementBorderStrokeWidth);
};

const nodeHasErrorBorder = (node) => {
  const borderColor = node.attr('body/stroke');
  return borderColor === RappidNode.TemplateErrorColor;
};

const isAutomationStepNode = (node) =>
  !!node?.attributes?.roiId?.startsWith?.(RappidNode.AutomationStep);

const isAddStepNode = (node) =>
  node?.attributes?.roiNodeType === RappidNode.AddStep ||
  node?.id?.startsWith?.(RappidNode.AddStep) ||
  node?.id?.startsWith?.(RappidNode.InitStep);

const isEndOfFlowNode = (node) =>
  node?.attributes?.roiNodeType === RappidNode.EndOfFlow ||
  node?.attributes?.roiNodeType === RappidNodeType.EndOfFlow ||
  node?.id?.startsWith?.(RappidNode.EndOfFlow);

const isTriggerNode = (node) =>
  node?.attributes?.roiNodeType === RappidNodeType.Trigger ||
  node?.attributes?.type === RappidNode.RoiCustomTrigger;

/** Immediate enclosing branch: 'root' | `${ifElseId}:yes` | `${ifElseId}:no` */
const getEnclosingBranchKey = (node) => {
  const { graph } = rappidStore;
  if (!graph || !node) return 'root';

  let current = node;
  while (current) {
    const inbound = graph.getNeighbors(current, { inbound: true }) || [];
    if (!inbound.length) return 'root';
    const parent = inbound[0];

    if (parent.attributes?.roiNodeType === RappidNodeType.IfElse) {
      // Prefer link label (YES/NO) — neighbor array order is not reliable
      const outLinks = graph.getConnectedLinks(parent, { outbound: true }) || [];
      const linkToCurrent = outLinks.find(link => {
        const targetId = link.getTargetCell()?.id ?? link.get('target')?.id;
        return targetId === current.id;
      });
      const labelText = (
        linkToCurrent?.label(0)?.attrs?.text?.text ||
        linkToCurrent?.label(0)?.attrs?.label?.text ||
        ''
      ).toString().toLowerCase();
      const side = labelText.includes('no') || labelText.includes('otherwise') ? 'no' : 'yes';
      return `${parent.id}:${side}`;
    }

    if (isTriggerNode(parent)) return 'root';
    current = parent;
  }
  return 'root';
};

const getStepTrailingAddStep = (step) => {
  const { graph } = rappidStore;
  const outbound = graph.getNeighbors(step, { outbound: true }) || [];
  return outbound.find(isAddStepNode) || outbound[0];
};

const canReorderAutomationStep = (step) => {
  if (!isAutomationStepNode(step)) return false;
  if (automationStore.automationState !== Enum.AutomationStateType.Draft) return false;
  return true;
};

/**
 * Linear steps may drop on any + except their own trailing + / current slot.
 * Detach-first surgery makes this cycle-safe across Yes/No and nested conditions.
 * If/Else blocks still cannot drop into their own subtree.
 */
const isValidReorderDropTarget = (step, targetAddStep) => {
  const { graph } = rappidStore;
  if (!graph || !step || !targetAddStep || !isAddStepNode(targetAddStep)) return false;

  const isIfElse = step.attributes?.roiNodeType === RappidNodeType.IfElse;
  const stepAdd = !isIfElse ? getStepTrailingAddStep(step) : null;

  if (stepAdd && targetAddStep.id === stepAdd.id) return false;

  const prevAdd = (graph.getNeighbors(step, { inbound: true }) || [])[0];
  if (prevAdd && prevAdd.id === targetAddStep.id) return false;

  if (isIfElse) {
    const ownSubtree = graph.getSuccessors(step) || [];
    if (ownSubtree.some(n => n.id === targetAddStep.id)) return false;
    // Existing If/Else move surgery expects an ancestor + (target leads to this node)
    return (graph.getSuccessors(targetAddStep) || []).some(n => n.id === step.id);
  }

  return true;
};

const ensureAddStepHasOutbound = (addStep) => {
  const { graph } = rappidStore;
  const outbound = graph.getNeighbors(addStep, { outbound: true }) || [];
  if (outbound.length) return;
  const pos = addStep.position();
  AddToCanvas_EndOfFlowNode(RappidNode.DistanceX, pos.y + RappidNode.DistanceY, addStep);
};

/**
 * Move a linear step (email/delay/…) including its trailing +.
 * Supports same-branch reorder, extracting out of a condition, and dropping into a branch.
 */
const MoveLinearStepAfterAddStep = (step, targetAddStep) => {
  const { graph } = rappidStore;
  if (!isValidReorderDropTarget(step, targetAddStep)) return false;

  const stepAdd = getStepTrailingAddStep(step);
  if (!stepAdd) return false;

  const prevAdd = (graph.getNeighbors(step, { inbound: true }) || [])[0];
  if (!prevAdd || !isAddStepNode(prevAdd)) return false;

  const nextAfterUnit = (graph.getNeighbors(stepAdd, { outbound: true }) || [])[0];
  const targetNext = (graph.getNeighbors(targetAddStep, { outbound: true }) || [])[0];

  // Detach unit: prevAdd → step → stepAdd → next  =>  prevAdd → next
  const inboundStepLinks = graph.getConnectedLinks(step, { inbound: true }) || [];
  const outboundStepAddLinks = graph.getConnectedLinks(stepAdd, { outbound: true }) || [];
  graph.removeCells([...inboundStepLinks, ...outboundStepAddLinks]);
  if (nextAfterUnit) {
    AddRappidLink(prevAdd, nextAfterUnit);
  } else {
    ensureAddStepHasOutbound(prevAdd);
  }

  // Insert after targetAddStep
  const targetOutLinks = graph.getConnectedLinks(targetAddStep, { outbound: true }) || [];
  graph.removeCells(targetOutLinks);
  AddRappidLink(targetAddStep, step);
  if (targetNext) {
    AddRappidLink(stepAdd, targetNext);
  } else {
    ensureAddStepHasOutbound(stepAdd);
  }

  return true;
};

/**
 * The If/Else's Yes-branch add-step, found by link label rather than neighbor array
 * order (which JointJS doesn't guarantee reflects Yes-first / No-second).
 */
const getYesBranchAddStep = (ifElse) => {
  const { graph } = rappidStore;
  const outLinks = graph.getConnectedLinks(ifElse, { outbound: true }) || [];
  const isNoLink = (link) => {
    const labelText = (
      link.label?.(0)?.attrs?.text?.text ||
      link.label?.(0)?.attrs?.label?.text ||
      ''
    ).toString().toLowerCase();
    return labelText.includes('no') || labelText.includes('otherwise');
  };
  const yesLink = outLinks.find(l => !isNoLink(l)) || outLinks[0];
  if (!yesLink) return null;
  const targetId = yesLink.getTargetCell?.()?.id ?? yesLink.get('target')?.id;
  return graph.getCell(targetId);
};

/**
 * Walks the Yes branch's linear chain to its last add-step (the one that currently
 * leads nowhere but an End of Flow), stopping early if it runs into a nested If/Else.
 */
const findYesBranchAppendPoint = (yesAdd) => {
  const { graph } = rappidStore;
  let currentAdd = yesAdd;
  while (currentAdd) {
    const next = (graph.getNeighbors(currentAdd, { outbound: true }) || [])[0];
    if (!next || isEndOfFlowNode(next) || next.attributes?.roiNodeType === RappidNodeType.IfElse) {
      return currentAdd;
    }
    const trailingAdd = getStepTrailingAddStep(next);
    if (!trailingAdd) return currentAdd;
    currentAdd = trailingAdd;
  }
  return yesAdd;
};

/**
 * Move an If/Else block (entire subtree, Yes and No branches untouched) earlier on
 * its branch, or to an ancestor +. Whatever used to follow the drop target is
 * appended to the end of the dragged condition's own Yes branch.
 */
const MoveIfElseAfterAddStep = (ifElse, targetAddStep) => {
  const { graph } = rappidStore;
  if (ifElse.attributes?.roiNodeType !== RappidNodeType.IfElse) return false;
  if (!isValidReorderDropTarget(ifElse, targetAddStep)) return false;

  const prevAdd = (graph.getNeighbors(ifElse, { inbound: true }) || [])[0];
  if (!prevAdd || !isAddStepNode(prevAdd)) return false;

  const yesAdd = getYesBranchAddStep(ifElse);
  if (!yesAdd) return false;

  const targetNext = (graph.getNeighbors(targetAddStep, { outbound: true }) || [])[0];

  // Detach the If/Else (its own Yes/No subtree moves with it) from prevAdd
  const inboundLinks = graph.getConnectedLinks(ifElse, { inbound: true }) || [];
  graph.removeCells(inboundLinks);
  ensureAddStepHasOutbound(prevAdd);

  // Place the If/Else after targetAddStep
  const targetOutLinks = graph.getConnectedLinks(targetAddStep, { outbound: true }) || [];
  graph.removeCells(targetOutLinks);
  AddRappidLink(targetAddStep, ifElse);

  // Append whatever used to follow targetAddStep onto the end of the Yes branch
  if (targetNext && targetNext.id !== ifElse.id) {
    const appendAdd = findYesBranchAppendPoint(yesAdd);
    const existingAfterAppend = (graph.getNeighbors(appendAdd, { outbound: true }) || [])[0];
    const appendOutLinks = graph.getConnectedLinks(appendAdd, { outbound: true }) || [];
    graph.removeCells(appendOutLinks);
    if (existingAfterAppend && isEndOfFlowNode(existingAfterAppend)) {
      graph.removeCells(existingAfterAppend);
    } else if (existingAfterAppend) {
      // The Yes branch continued into a nested If/Else (or other real content) rather
      // than dead-ending in an End of flow - keep it, reattached after the displaced
      // chain instead of being orphaned. prevAdd is the displaced chain's own tail,
      // just given a placeholder End of flow by ensureAddStepHasOutbound above.
      const prevAddEnd = (graph.getNeighbors(prevAdd, { outbound: true }) || []).filter(isEndOfFlowNode);
      graph.removeCells([...graph.getConnectedLinks(prevAdd, { outbound: true }), ...prevAddEnd]);
      AddRappidLink(prevAdd, existingAfterAppend);
    }
    AddRappidLink(appendAdd, targetNext);
  }

  return true;
};

/**
 * Move an automation step so it sits after targetAddStep.
 */
const MoveAutomationStepAfterAddStep = (step, targetAddStep) => {
  const { graph, tree } = rappidStore;
  if (!graph || !step || !targetAddStep) return false;
  if (!canReorderAutomationStep(step)) return false;
  if (!isAddStepNode(targetAddStep)) return false;

  const moved = step.attributes?.roiNodeType === RappidNodeType.IfElse
    ? MoveIfElseAfterAddStep(step, targetAddStep)
    : MoveLinearStepAfterAddStep(step, targetAddStep);

  if (!moved) return false;

  tree.layout();
  automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure());
  automationStore.setIsAutomationFormDirty(true);
  return true;
};

const getValidReorderDropTargets = (step) => {
  const { graph } = rappidStore;
  if (!graph || !canReorderAutomationStep(step)) return [];

  return graph.getElements().filter(el => isValidReorderDropTarget(step, el));
};

/**
 * A linear step occupies a slot: prevAdd → step → trailingAdd → next.
 * Both the step and its trailing + travel together when the slot changes.
 */
const getLinearStepUnit = (step) => {
  const { graph } = rappidStore;
  if (!graph || !step) return null;

  const prevAdd = (graph.getNeighbors(step, { inbound: true }) || [])[0];
  const trailingAdd = getStepTrailingAddStep(step);
  if (!prevAdd || !isAddStepNode(prevAdd) || !trailingAdd || !isAddStepNode(trailingAdd)) return null;

  return {
    step,
    prevAdd,
    trailingAdd,
    next: (graph.getNeighbors(trailingAdd, { outbound: true }) || [])[0],
  };
};

/**
 * Two linear steps may trade slots, including across branches. If/Else blocks are
 * excluded: their content lives inside the Yes/No subtree, so they have no single
 * slot to trade — they keep using the drop-on-+ path.
 */
const isValidReorderSwapTarget = (step, target) => {
  if (!step || !target || step.id === target.id) return false;
  if (!canReorderAutomationStep(step)) return false;
  if (!isAutomationStepNode(target)) return false;
  if (step.attributes?.roiNodeType === RappidNodeType.IfElse) return false;
  if (target.attributes?.roiNodeType === RappidNodeType.IfElse) return false;
  return !!getLinearStepUnit(step) && !!getLinearStepUnit(target);
};

const getValidReorderSwapTargets = (step) => {
  const { graph } = rappidStore;
  if (!graph || !canReorderAutomationStep(step)) return [];

  return graph.getElements().filter(el => isValidReorderSwapTarget(step, el));
};

/** Reconnect a trailing + to whatever followed it, keeping the dashed end-of-flow line. */
const linkAfterUnit = (trailingAdd, next) => {
  if (next) {
    AddRappidLink(trailingAdd, next, isEndOfFlowNode(next));
  } else {
    ensureAddStepHasOutbound(trailingAdd);
  }
};

/**
 * Swap two linear steps: each one (with its trailing +) takes the other's slot.
 */
const SwapAutomationSteps = (stepA, stepB) => {
  const { graph, tree } = rappidStore;
  if (!graph || !isValidReorderSwapTarget(stepA, stepB)) return false;

  const a = getLinearStepUnit(stepA);
  const b = getLinearStepUnit(stepB);
  if (!a || !b) return false;

  const staleLinks = [
    ...graph.getConnectedLinks(a.step, { inbound: true }),
    ...graph.getConnectedLinks(a.trailingAdd, { outbound: true }),
    ...graph.getConnectedLinks(b.step, { inbound: true }),
    ...graph.getConnectedLinks(b.trailingAdd, { outbound: true }),
  ];
  graph.removeCells(_.uniqBy(staleLinks, 'id'));

  const isAThenB = a.trailingAdd.id === b.prevAdd.id;
  const isBThenA = b.trailingAdd.id === a.prevAdd.id;

  if (isAThenB) {
    // …prevAdd → B → trailingAddB → A → trailingAddA → next…
    AddRappidLink(a.prevAdd, b.step);
    AddRappidLink(b.trailingAdd, a.step);
    linkAfterUnit(a.trailingAdd, b.next);
  } else if (isBThenA) {
    AddRappidLink(b.prevAdd, a.step);
    AddRappidLink(a.trailingAdd, b.step);
    linkAfterUnit(b.trailingAdd, a.next);
  } else {
    AddRappidLink(a.prevAdd, b.step);
    linkAfterUnit(b.trailingAdd, a.next);
    AddRappidLink(b.prevAdd, a.step);
    linkAfterUnit(a.trailingAdd, b.next);
  }

  tree.layout();
  automationStore.setAutomationDelayArrayStructure(ConvertToDelayArrayStructure());
  automationStore.setIsAutomationFormDirty(true);
  return true;
};

/** Condition blocks own a Yes/No subtree, so they are not duplicable. */
const canDuplicateAutomationStep = (step) => {
  if (!isAutomationStepNode(step)) return false;
  if (step.attributes?.roiNodeType === RappidNodeType.IfElse) return false;
  return automationStore.automationState === Enum.AutomationStateType.Draft;
};

/** Insert a copy of a step, with its own copy of the step data, directly below it. */
const DuplicateNode = (step) => {
  const { graph } = rappidStore;
  if (!graph || !canDuplicateAutomationStep(step)) return null;

  const trailingAdd = getStepTrailingAddStep(step);
  if (!trailingAdd || !isAddStepNode(trailingAdd)) return null;

  const roiNodeType = step.attributes.roiNodeType;
  const roiData = _.cloneDeep(toJS(step.attributes.roiData)) || {};
  const [text1, text2, text3, text4] = getNodeTextLines(roiNodeType, roiData);

  // isUserAddedNode stays false so a copy of an unvalidated template step keeps its error border.
  const duplicated = AddNodeToCanvas(roiNodeType, text1, text2, text3, text4, roiData, trailingAdd, null, false, true);
  automationStore.setIsAutomationFormDirty(true);
  return duplicated;
};

const validateNodeAndResetBorder = () => {
  const { node } = rappidStore;
  if (!node) return false;
  if (nodeHasErrorBorder(node)) {
    resetBorderForNode(node.id);
    return true;
  }
  return false;
};

export {
  CANVAS_STRIPPED_OPTION_KEYS,
  BuildCanvasJson,
  UpdateCompletedStepText,
  AddNodeToCanvas,
  EditRappidNode,
  CheckIfRappidCanvasIsEmpty,
  CheckIfFirstTriggerCanvasIsEmpty,
  GetDelayId,
  BuildArrayStructure,
  ConvertToArrayStructure,
  ConvertToDelayArrayStructure,
  BuildTriggerList,
  ConvertTriggerListForBackEnd,
  DeleteNode,
  AddEmptyTriggerNodeToCanvas,
  DeleteTrigger,
  RedrawCanvas,
  ResetCanvasDelayDate,
  ResetDelayDate,
  GetArrayStructure,
  loadAutomationIntoCanvas,
  refreshEmailSenderNames,
  loadAutomationReportIntoCanvas,
  AddEmptyTriggerNodeToReportCanvas,
  getAllNodes,
  getNodesInfo,
  setBorderColorForNode,
  resetBorderForNode,
  setErrorBorder,
  getTriggerNodeData,
  nodeHasErrorBorder,
  validateNodeAndResetBorder,
  MoveAutomationStepAfterAddStep,
  getValidReorderDropTargets,
  SwapAutomationSteps,
  getValidReorderSwapTargets,
  canReorderAutomationStep,
  DuplicateNode,
  canDuplicateAutomationStep,
  isAddStepNode,
};
